import React, { Suspense } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { FooterTypes } from 'components/footer/footer.types';
import AuthLayout from 'layout/AuthLayout';
import {
  AUTH_EMAIL_NOT_VERIFICATION,
  AUTH_MFA_PROTECT,
  BASE_ROUTE,
  CREATE_PROVIDER,
  EMAIL_NOT_VERIFICATION,
} from 'utilities/routes';
import NetworkRequiredRoutes from 'components/networkRequiredRoutes';
import { FallbackLoader } from '../../components/loaderComponent';

const Login = React.lazy(() => import('auth/pages/Login/Login'));
const EmailNotVerified = React.lazy(
  () => import('auth/pages/EmailNotVerified/EmailNotVerified'),
);
const CreateProvider = React.lazy(() => import('auth/pages/CreateProvider'));
const SignUp = React.lazy(() => import('auth/pages/SignUp/SignUp'));
const Verification = React.lazy(
  () => import('auth/pages/Verification/Verification'),
);
const ForgotPassword = React.lazy(
  () => import('auth/pages/ForgotPassword/ForgotPassword'),
);
const ResetPassword = React.lazy(
  () => import('auth/pages/ResetPassword/ResetPassword'),
);
const Action = React.lazy(() => import('auth/pages/Action/Action'));
const SendVerificationEmail = React.lazy(
  () => import('auth/pages/SendVerificationEmail/SendVerificationEmail'),
);
const SendResetPasswordEmail = React.lazy(
  () => import('auth/pages/SendResetPasswordEmail/SendResetPasswordEmail'),
);
const MultiFactorAuthVerify = React.lazy(
  () => import('auth/pages/MultiFactorAuth/MultiFactorAuthVerify'),
);
const MultiFactorAuthSetup = React.lazy(
  () => import('auth/pages/MultiFactorAuth/MultiFactorAuthSetup'),
);
const MultiFactorAuthConfirmProtect = React.lazy(
  () => import('auth/pages/MultiFactorAuth/MultiFactorAuthConfirmProtect'),
);
const ExpiredResetPassword = React.lazy(
  () => import('auth/pages/ExpiredResetPassword/ExpiredResetPassword'),
);

export enum ContentSize {
  SMALL = 'desktop:w-[40%]',
  DEFAULT = 'desktop:w-[48%]',
  LARGE = 'desktop:w-[59%]',
  FULL_WIDTH = 'desktop:w-full',
  LOGIN = 'auth-login__width',
  FIXED = 'desktop:w-[690px]',
}

export const AuthRoutes = () => {
  const location = useLocation();

  const isSignUpOrMfaRoute = [
    '/auth/sign-up',
    '/auth/create-provider',
    AUTH_MFA_PROTECT,
    '/auth/mfa-setup',
    AUTH_EMAIL_NOT_VERIFICATION,
    '/auth/send-verification-email',
  ].includes(location.pathname);

  const bgImageClass = isSignUpOrMfaRoute
    ? 'bg-register-m bg-contain desktop:bg-register'
    : 'bg-login-m bg-cover desktop:bg-login';

  let contentSize: ContentSize = ContentSize.DEFAULT;
  let footerType: FooterTypes;

  switch (location.pathname) {
    case '/auth/sign-up':
    case '/auth/create-provider':
      footerType = 'signup';
      break;
    case AUTH_EMAIL_NOT_VERIFICATION:
      footerType = 'signup';
      contentSize = ContentSize.FIXED;
      break;
    case '/auth/sign-up-provider':
      contentSize = ContentSize.FULL_WIDTH;
      footerType = 'default';
      break;
    case '/auth/mfa-setup':
    case AUTH_MFA_PROTECT:
      contentSize = ContentSize.LARGE;
      footerType = 'signup';
      break;
    case '/auth/login':
      contentSize = ContentSize.LOGIN;
      footerType = 'login';
      break;
    case '/auth/mfa-verify':
      contentSize = ContentSize.LARGE;
      footerType = 'login';
      break;
    case '/auth/forgot-password':
    case '/auth/reset-password':
    case '/auth/verification':
    case '/auth/send-reset-password-email':
    case '/auth/reset-password-expired-link':
      footerType = 'login';
      break;
    case '/auth/send-verification-email':
      footerType = 'signup';
      break;
    default:
      footerType = 'default';
      break;
  }

  return (
    <AuthLayout
      background={bgImageClass}
      footerType={footerType}
      contentSize={contentSize}
    >
      <Suspense fallback={<FallbackLoader />}>
        <NetworkRequiredRoutes>
          <Routes>
            <Route path={BASE_ROUTE} element={<Navigate to="/login" />} />
            <Route path="/login" element={<Login />} />
            <Route path="/sign-up" element={<SignUp />} />
            <Route path={CREATE_PROVIDER} element={<CreateProvider />} />
            <Route
              path={EMAIL_NOT_VERIFICATION}
              element={<EmailNotVerified />}
            />
            <Route path="/mfa-verify" element={<MultiFactorAuthVerify />} />
            <Route path="/mfa-setup" element={<MultiFactorAuthSetup />} />
            <Route
              path="/mfa-protect"
              element={<MultiFactorAuthConfirmProtect />}
            />
            <Route
              path="/send-verification-email"
              element={<SendVerificationEmail />}
            />
            <Route
              path="/send-reset-password-email"
              element={<SendResetPasswordEmail />}
            />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/reset-password-expired-link"
              element={<ExpiredResetPassword />}
            />
            <Route path="/verification" element={<Verification />} />
            <Route path="/action" element={<Action />} />
            <Route path="/*" element={<Navigate to="/" />} />
          </Routes>
        </NetworkRequiredRoutes>
      </Suspense>
    </AuthLayout>
  );
};
